import { db } from '../lib/firebase';
import { collection, doc, getDoc, getDocs, query, where, setDoc, addDoc, updateDoc, serverTimestamp, increment } from 'firebase/firestore';
import { getMarketerByReferralCode, recordMarketerReferral } from './marketers';

export interface ReferralRecord {
  userId: string;
  name?: string | null;
  email?: string | null;
  createdAt: any; // Firestore Timestamp
}

function randomCode(length = 7) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < length; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export async function generateUniqueReferralCode(): Promise<string> {
  // Try a few times to avoid collisions
  for (let i = 0; i < 5; i++) {
    const code = randomCode(7);
    const qRef = query(collection(db, 'users'), where('referralCode', '==', code));
    const snap = await getDocs(qRef);
    if (snap.empty) return code;
  }
  // Fallback include timestamp fragment
  return randomCode(5) + String(Date.now()).slice(-2);
}

export async function getUserIdByReferralCode(code: string): Promise<string | null> {
  const qRef = query(collection(db, 'users'), where('referralCode', '==', code));
  const snap = await getDocs(qRef);
  if (!snap.empty) {
    const docSnap = snap.docs[0];
    return docSnap.id;
  }
  return null;
}

export async function ensureReferralCode(uid: string): Promise<string> {
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  const existing = snap.exists() ? (snap.data() as any).referralCode : null;
  if (existing && typeof existing === 'string') return existing;
  const code = await generateUniqueReferralCode();
  await setDoc(userRef, { referralCode: code }, { merge: true });
  return code;
}

export async function recordReferral(referrerUid: string, referredUid: string): Promise<void> {
  // Get referred user's basic info
  const referredRef = doc(db, 'users', referredUid);
  const referredSnap = await getDoc(referredRef);
  const referredData = referredSnap.exists() ? (referredSnap.data() as any) : {};

  // Write subcollection record under referrer
  const subRef = collection(doc(db, 'users', referrerUid), 'referrals');
  await addDoc(subRef, {
    userId: referredUid,
    name: referredData.fullName || null,
    email: referredData.email || null,
    createdAt: serverTimestamp(),
  } as ReferralRecord);

  // Increment counter on referrer profile
  try {
    await updateDoc(doc(db, 'users', referrerUid), { referralsCount: increment(1) });
  } catch {
    await setDoc(doc(db, 'users', referrerUid), { referralsCount: increment(1) }, { merge: true });
  }
}

export async function resolveReferrerByCode(code: string): Promise<{ type: 'user'; id: string } | { type: 'marketer'; id: string } | null> {
  const userId = await getUserIdByReferralCode(code);
  if (userId) return { type: 'user', id: userId };
  const marketer = await getMarketerByReferralCode(code);
  if (marketer?.id) return { type: 'marketer', id: marketer.id };
  return null;
}

export async function recordReferralByType(referrer: { type: 'user' | 'marketer'; id: string }, referredUid: string): Promise<void> {
  if (referrer.type === 'user') {
    await recordReferral(referrer.id, referredUid);
  } else {
    await recordMarketerReferral(referrer.id, referredUid);
  }
}